/* CursorTrail — listens to mousemove events globally and spawns small
 * fading particles based on the active trail recipe. Pure JS so it
 * survives React re-renders without leaking listeners.
 *
 * Performance:
 *   - Throttles to 60 spawns/sec max (one per requestAnimationFrame)
 *   - Each particle auto-removes itself after `life` ms
 *   - Caps live particles at 80; oldest get pruned first
 *   - Uses absolute positioning in a single root <div> appended to body
 */
import { useEffect } from "react";

const ROOT_ID = "vault-cursor-trail-root";
const HUE_STREAM = [0, 30, 60, 120, 180, 240, 300]; // rainbow cycle

export default function CursorTrail({ trail }) {
  useEffect(() => {
    if (!trail || !trail.recipe) return undefined;
    const recipe = trail.recipe;

    let root = document.getElementById(ROOT_ID);
    if (!root) {
      root = document.createElement("div");
      root.id = ROOT_ID;
      root.style.cssText =
        "position:fixed;inset:0;pointer-events:none;z-index:9998;overflow:hidden;";
      // IMPORTANT: append to <html>, not <body>. Eclipse Mode (and any future
      // theme effect) applies `filter:` to <body>, which per CSS spec causes
      // `position: fixed` descendants to be containing-block-relative to that
      // filtered ancestor instead of the viewport — particles then spawn at
      // viewport coords inside a container that's been pinned to document Y=0,
      // so once the user scrolls, trails appear stuck at the top of the page.
      // Mounting on <html> sidesteps the filter ancestor entirely.
      document.documentElement.appendChild(root);
    }

    let liveParticles = [];
    let lastSpawn = 0;
    let hueIdx = 0;

    const spawn = (x, y) => {
      const now = performance.now();
      if (now - lastSpawn < 16) return; // 60Hz cap
      lastSpawn = now;
      const count = recipe.count || 1;
      for (let i = 0; i < count; i++) {
        const el = document.createElement("span");
        const jitterX = (Math.random() - 0.5) * 14;
        const jitterY = (Math.random() - 0.5) * 14;
        const size = recipe.size || 10;
        let color = recipe.color || "#ffffff";
        if (recipe.kind === "rainbow") {
          color = `hsl(${HUE_STREAM[hueIdx % HUE_STREAM.length]}, 100%, 60%)`;
          hueIdx += 1;
        }
        let inner = "";
        if (recipe.kind === "glyph") {
          el.textContent = recipe.glyph;
          el.style.fontSize = `${size}px`;
          el.style.color = color;
          el.style.textShadow = `0 0 6px ${color}`;
        } else if (recipe.kind === "blob") {
          el.style.width = `${size}px`;
          el.style.height = `${size}px`;
          el.style.borderRadius = "50%";
          el.style.background = `radial-gradient(circle, ${color}cc, ${color}00 70%)`;
        } else if (recipe.kind === "rainbow") {
          el.style.width = `${size}px`;
          el.style.height = `${size}px`;
          el.style.borderRadius = "50%";
          el.style.background = `radial-gradient(circle, ${color}, transparent 70%)`;
        } else if (recipe.kind === "bar") {
          el.style.width = `${size}px`;
          el.style.height = "4px";
          el.style.background = color;
          el.style.borderRadius = "1px";
        } else if (recipe.kind === "dots") {
          el.style.width = `${size}px`;
          el.style.height = `${size}px`;
          el.style.borderRadius = "50%";
          el.style.background = color;
        }
        el.style.position = "absolute";
        el.style.left = `${x + jitterX - size / 2}px`;
        el.style.top = `${y + jitterY - size / 2}px`;
        el.style.pointerEvents = "none";
        el.style.transition = `opacity ${recipe.life}ms linear, transform ${recipe.life}ms linear`;
        el.style.willChange = "opacity, transform";
        el.style.opacity = "1";
        root.appendChild(el);
        liveParticles.push(el);
        // animate next frame so transition fires
        requestAnimationFrame(() => {
          el.style.opacity = "0";
          el.style.transform = `translate(${jitterX}px, ${(recipe.kind === "glyph" || recipe.kind === "bar") ? recipe.life / 30 : 0}px) scale(0.6)`;
        });
        setTimeout(() => {
          if (el.parentNode) el.parentNode.removeChild(el);
        }, recipe.life + 80);
      }
      // Cap live particles defensively
      if (liveParticles.length > 80) {
        const stale = liveParticles.splice(0, liveParticles.length - 80);
        stale.forEach((s) => { if (s.parentNode) s.parentNode.removeChild(s); });
      }
    };

    const onMove = (e) => spawn(e.clientX, e.clientY);
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      // Clean particle root entirely so swapping trails doesn't double-stack
      if (root && root.parentNode) root.parentNode.removeChild(root);
    };
  }, [trail]);

  return null;
}
